/**
 * FilterCollapsedSidebar
 *
 * A shared reusable component that renders the collapsed state of any filter
 * sidebar across the project. It provides a standardised icon-container header
 * (height, padding, background, border) so that every collapsed sidebar looks
 * identical and a single change here updates the entire application.
 *
 * Design spec:
 *   - Container height  : 64px  (FILTER_COLLAPSED_ICON_CONTAINER_HEIGHT)
 *   - Horizontal padding: 24px  (px-6)
 *   - Vertical padding  : 22px  (py-[22px])
 *   - Icon size         : 20×20px
 *   - Background        : #F8FAFC (bg-filter-collapsed-bg)
 *   - Border            : bottom, sidebarHeaderBorder token
 *   - Width             : fills the container (full-width divider, end-to-end)
 *
 * Usage variants:
 *
 * 1. Static (no click handler) — just shows the icon:
 *    <FilterCollapsedSidebar className="h-[calc(100vh-4rem)] bg-surface">
 *      {optional extra content below the icon container}
 *    </FilterCollapsedSidebar>
 *
 * 2. Interactive (icon container is a button) — calls onToggle when clicked:
 *    <FilterCollapsedSidebar onToggle={openSidebar} className="bg-surface">
 *      {optional extra content below the icon container}
 *    </FilterCollapsedSidebar>
 */
import React from 'react';
import { Filter } from 'lucide-react';
import { FILTER_COLLAPSED_ICON_CONTAINER_HEIGHT } from '@/shared/constants/layout';

export interface FilterCollapsedSidebarProps {
  /**
   * If provided, the icon container is rendered as a `<button>` that calls
   * `onToggle` when clicked. If omitted, it is rendered as a plain `<div>`.
   */
  onToggle?: () => void;
  /**
   * Additional Tailwind classes applied to the outer wrapper `<div>`.
   * Use this to control the height and background of the full sidebar area.
   * @example "h-[calc(100vh-4rem)] bg-surface"
   */
  className?: string;
  /**
   * Optional content rendered below the icon-container header.
   * Useful for chevron expand/collapse buttons or decorative icons.
   */
  children?: React.ReactNode;
  /** aria-label for the icon button when `onToggle` is provided. */
  ariaLabel?: string;
  /** data-testid forwarded to the icon container element. */
  testId?: string;
}

const ICON_CONTAINER_CLASS =
  'flex items-center justify-center w-full px-6 py-[22px] bg-filter-collapsed-bg border-b border-sidebarHeaderBorder';

export const FilterCollapsedSidebar: React.FC<FilterCollapsedSidebarProps> = ({
  onToggle,
  className = 'bg-surface',
  children,
  ariaLabel = 'Open filters sidebar',
  testId,
}) => {
  const containerStyle = { height: FILTER_COLLAPSED_ICON_CONTAINER_HEIGHT };
  const icon = <Filter size={20} className="text-primary" />;

  return (
    <div className={`flex flex-col items-center ${className}`}>
      {onToggle ? (
        <button
          type="button"
          onClick={onToggle}
          className={`${ICON_CONTAINER_CLASS} focus:outline-none`}
          style={containerStyle}
          aria-label={ariaLabel}
          data-testid={testId}
        >
          {icon}
        </button>
      ) : (
        <div
          className={ICON_CONTAINER_CLASS}
          style={containerStyle}
          data-testid={testId}
        >
          {icon}
        </div>
      )}
      {children}
    </div>
  );
};

export default FilterCollapsedSidebar;